/**
 * Frequently Bought Together – Rebuy API + Add All to Cart
 * Vanilla JS custom element; uses theme.routes and theme.settings.
 */
(function () {
  'use strict';

  const getRebuyData = async (additionalParams) => {
    const { ruleId = '', productId, limit = 10, key, ...otherParams } = additionalParams || {};
    if (!ruleId) return { results: [] };
    const url = new URL(`https://rebuyengine.com/api/v1/custom/id/${ruleId}`);
    const params = {
      limit: limit,
      filter_oos: 'yes',
      context: 'default',
      metafields: 'yes',
      ...otherParams
    };
    if (key) params.key = key;
    if (productId) params.shopify_product_ids = String(productId);
    Object.keys(params).forEach((param) => {
      url.searchParams.append(param, params[param]);
    });
    const data = await fetch(url);
    const productData = await data.json();
    return productData;
  };

  const getRebuySettings = async (widgetId) => {
    if (!widgetId) return null;
    try {
      const url = `https://rebuyengine.com/api/v1/widgets/settings?id=${widgetId}`;
      const response = await fetch(url);
      const settingsData = await response.json();
      return settingsData;
    } catch (error) {
      console.error('Error fetching Rebuy settings:', error);
      return null;
    }
  };

  function formatMoney(cents, format) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, format);
    }
    const formatStr = format || window.theme?.settings?.money_with_currency_format || '${{amount}}';
    const value = (Number(cents) / 100).toFixed(2);
    return formatStr.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function getMoneyFormat() {
    return window.theme?.settings?.money_with_currency_format || '${{amount}}';
  }

  /**
   * Normalize Rebuy product: support results[] or data[] and common field names.
   * Rebuy may return price in dollars (e.g. 47.99) or cents; we store cents for formatMoney.
   */
  function normalizeProducts(payload) {
    let list = payload?.results || payload?.data || payload?.products || [];
    if (!Array.isArray(list)) list = [];
    return list.map((item) => {
      const id = item.id ?? item.shopify_product_id;
      const rawPrice = item.variants?.[0]?.price ?? item.price ?? item.price_min ?? 0;
      const num = Number(rawPrice);
      const price = num > 0 && num < 1000 && num !== Math.floor(num) ? Math.round(num * 100) : num;
      const image = item.image?.src ?? item.featured_image ?? item.image ?? item.featured_image_url ?? '';
      const imageUrl = typeof image === 'string' ? image : (image?.url || '');
      const variants = item.variants || [];
      const variantId = variants.length ? (variants[0].id ?? variants[0].variant_id) : null;
      return {
        id: id,
        title: item.title || '',
        handle: item.handle || '',
        vendor: item.vendor || '',
        price: price,
        compare_at_price: item.compare_at_price ?? item.compare_at_price_min,
        image_url: imageUrl,
        url: item.url || (item.handle ? ` /products/${item.handle}` : ''),
        variant_id: variantId,
        available: item.available !== false
      };
    });
  }

  function getSectionsToRender() {
    return [
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
    ];
  }

  function renderSectionContents(parsedState) {
    const getSectionInnerHTML = (html, selector) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector(selector);
      return el ? el.innerHTML : '';
    };
    getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      const html = parsedState.sections?.[section.section];
      if (!html) return;
      const inner = getSectionInnerHTML(html, section.selector);
      const target = el.querySelector(section.selector) || el;
      if (inner) target.innerHTML = inner;
      if (typeof window.CartDrawer !== 'undefined') {
        try { new window.CartDrawer(); } catch (e) {}
      }
    });
  }

  class FrequentlyBoughtTogether extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.settings = null;
      this.selected = new Set();
      this.state = { loading: true, error: null };
    }

    static get observedAttributes() {
      return ['data-rule-id', 'data-product-id', 'data-limit', 'data-api-key'];
    }

    connectedCallback() {
      const ruleId = this.dataset.ruleId;
      const productId = this.dataset.productId;
      if (!ruleId || !productId) {
        this.renderEmpty();
        return;
      }
      this.load(ruleId, productId);
    }

    getCurrentProductFromPage() {
      const container = this.closest('.frequently-bought-together') || this.parentElement;
      const script = container?.querySelector('script[data-fbt-current-product]');
      if (!script || !script.textContent.trim()) return null;
      try {
        const data = JSON.parse(script.textContent);
        return {
          id: data.id,
          title: data.title || '',
          vendor: data.vendor || '',
          price: Number(data.price) || 0,
          compare_at_price: data.compare_at_price ? Number(data.compare_at_price) : null,
          image_url: data.image_url || '',
          url: data.url || '',
          variant_id: data.variant_id,
          available: data.available !== false,
          type: 'primary'
        };
      } catch (e) {
        return null;
      }
    }

    async load(ruleId, productId) {
      const rebuyLimit = parseInt(this.dataset.limit, 10) || 2;
      const apiKey = this.dataset.apiKey || '';
      const widgetId = this.dataset.widgetId || '';

      this.setState({ loading: true, error: null });
      this.renderLoading();

      const currentProduct = this.getCurrentProductFromPage();

      try {
        const [payload, widgetSettings] = await Promise.all([
          getRebuyData({
            ruleId,
            productId,
            limit: rebuyLimit,
            key: apiKey
          }),
          widgetId ? getRebuySettings(widgetId) : Promise.resolve(null)
        ]);

        this.settings = widgetSettings;
        let rebuyProducts = normalizeProducts(payload);
        const currentId = currentProduct ? String(currentProduct.id) : null;
        if (currentId) {
          rebuyProducts = rebuyProducts.filter((p) => String(p.id) !== currentId);
        }
        this.products = currentProduct ? [currentProduct, ...rebuyProducts] : rebuyProducts;
        this.selected = new Set(this.products.map((p) => String(p.variant_id || p.id)).filter(Boolean));

        if (this.products.length === 0) {
          this.renderEmpty();
          return;
        }

        this.setState({ loading: false });
        this.render();
        this.attachListeners();
      } catch (err) {
        console.error('Frequently Bought Together:', err);
        this.setState({ loading: false, error: err.message });
        this.renderError();
      }
    }

    setState(next) {
      this.state = { ...this.state, ...next };
    }

    /**
     * Discount from Rebuy widget settings (same as 10x): settings.data.discount.amount, settings.data.discount.type.
     * type: 'percentage' | 'fixed'. amount: number (percent or cents off).
     */
    getDiscountFromRebuy() {
      const discount = this.settings?.data?.discount;
      const amount = Number(discount?.amount) || 0;
      const type = (discount?.type || 'percentage').toLowerCase();
      return { amount, type };
    }

    getSelectedProducts() {
      return this.products.filter((p) => {
        const key = String(p.variant_id || p.id);
        return key && this.selected.has(key);
      });
    }

    getBundleTotals() {
      const selected = this.getSelectedProducts();
      let originalCents = 0;
      let discountedCents = 0;
      selected.forEach((p) => {
        const priceCents = Number(p.price) || 0;
        originalCents += priceCents;
        discountedCents += priceCents;
      });
      const allSelected = selected.length === this.products.length;
      const { amount: discountAmount, type: discountType } = this.getDiscountFromRebuy();
      let discountPercent = 0;
      if (allSelected && discountAmount > 0) {
        if (discountType === 'percentage') {
          discountPercent = discountAmount;
          discountedCents = 0;
          selected.forEach((p) => {
            const priceCents = Number(p.price) || 0;
            const itemDiscountCents = Math.floor(priceCents * discountPercent / 100);
            discountedCents += priceCents - itemDiscountCents;
          });
        } else if (discountType === 'fixed') {
          const discountCentsTotal = Math.floor(Number(discountAmount));
          discountedCents = Math.max(0, originalCents - discountCentsTotal);
          discountPercent = originalCents > 0 ? Math.round(((originalCents - discountedCents) / originalCents) * 100) : 0;
        }
      }
      return { originalCents, discountedCents, discountPercent, count: selected.length };
    }

    renderLoading() {
      this.innerHTML = `
        <div class="fbt__placeholder row">
          <div class="small-12 columns">
            <p class="fbt__loading" aria-live="polite">Loading...</p>
          </div>
        </div>
      `;
    }

    renderEmpty() {
      this.innerHTML = '';
    }

    renderError() {
      this.innerHTML = `
        <div class="fbt__placeholder row">
          <div class="small-12 columns">
            <p class="fbt__error">Unable to load recommendations.</p>
          </div>
        </div>
      `;
    }

    render() {
      const heading = (this.dataset.heading || 'Frequently Bought Together').trim();
      const subHeading = (this.dataset.subHeading || '').trim();
      const format = getMoneyFormat();
      const selected = this.getSelectedProducts();
      const { originalCents, discountedCents, discountPercent } = this.getBundleTotals();
      const hasDiscount = discountPercent > 0 && selected.length > 0;

      let productsHtml = '';
      this.products.forEach((p, index) => {
        const key = String(p.variant_id || p.id);
        const checked = key && this.selected.has(key);
        const priceFormatted = formatMoney(p.price, format);
        const vendor = (p.vendor || '').toUpperCase();
        const isPrimary = index === 0;
        productsHtml += `
          ${index > 0 ? '<div class="fbt__plus" aria-hidden="true">+</div>' : ''}
          <div class="fbt__product" data-variant-id="${p.variant_id || ''}" data-product-id="${p.id || ''}" ${isPrimary ? ' data-fbt-primary' : ''}>
            <div class="fbt__product-media">
              <a href="${p.url || '#'}" class="fbt__product-link">
                <span class="fbt__product-image-wrap">
                  ${p.image_url ? `<img class="fbt__product-image" src="${p.image_url}" alt="${escapeAttr(p.title)}" loading="lazy" width="200" height="200">` : '<span class="fbt__product-image fbt__product-image--placeholder"></span>'}
                </span>
              </a>
              <button type="button" class="fbt__product-check ${checked ? 'fbt__product-check--checked' : ''} ${isPrimary ? ' fbt__product-check--primary' : ''}" aria-label="${checked ? 'Selected' : 'Not selected'}" data-fbt-toggle ${isPrimary ? ' disabled aria-disabled="true"' : ''}>
                ${checked ? '<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
              </button>
            </div>
            <div class="fbt__product-info">
              ${vendor ? `<div class="fbt__product-vendor">${escapeHtml(vendor)}</div>` : ''}
              <div class="fbt__product-title">${escapeHtml(p.title)}</div>
              <div class="fbt__product-price">${priceFormatted}</div>
            </div>
          </div>
        `;
      });

      const bundleHtml = selected.length
        ? `
          <div class="fbt__bundle">
            <div class="fbt__bundle-price-row">
              <span class="fbt__bundle-label">Bundle Price:</span>
              ${hasDiscount ? `<del class="fbt__bundle-original">${formatMoney(originalCents, format)}</del>` : ''}
              <span class="fbt__bundle-total">${formatMoney(discountedCents, format)}</span>
              ${hasDiscount ? `<span class="fbt__bundle-badge">${discountPercent}% OFF</span>` : ''}
            </div>
            <button type="button" class="fbt__add-all button accent2 full pill" data-fbt-add-all>
              <span>ADD ALL TO CART</span>
              <span class="loading-overlay"><svg class="spinner" aria-hidden="true" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="currentColor"></circle></svg></span>
            </button>
          </div>
        `
        : '';

      this.innerHTML = `
        <div class="row">
          <div class="small-12 columns">
            <h4 class="fbt__heading">${escapeHtml(heading)}</h4>
            ${subHeading ? `<p class="fbt__sub-heading">${escapeHtml(subHeading)}</p>` : ''}
          </div>
        </div>
        <div class="fbt__inner row align-middle">
          <div class="fbt__products small-12 medium-8 large-9 columns">
            ${productsHtml}
          </div>
          <div class="fbt__bundle-wrap small-12 medium-4 large-3 columns">
            ${bundleHtml}
          </div>
        </div>
      `;
    }

    attachListeners() {
      const container = this;
      const productEls = this.querySelectorAll('.fbt__product');
      const primaryKey = container.products.length ? String(container.products[0].variant_id || container.products[0].id) : '';

      productEls.forEach((el) => {
        const toggleBtn = el.querySelector('[data-fbt-toggle]');
        const key = String(el.dataset.variantId || el.dataset.productId || '');
        const isPrimary = el.hasAttribute('data-fbt-primary');
        if (!key) return;

        const toggleCheck = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (key === primaryKey) return;
          if (container.selected.has(key)) {
            container.selected.delete(key);
          } else {
            container.selected.add(key);
          }
          container.render();
          container.attachListeners();
        };

        if (toggleBtn && !isPrimary) {
          toggleBtn.addEventListener('click', toggleCheck);
        }

        if (!isPrimary) {
          const link = el.querySelector('.fbt__product-link');
          const imageWrap = el.querySelector('.fbt__product-image-wrap');
          if (link && imageWrap) {
            link.addEventListener('click', (e) => {
              if (e.target.closest('.fbt__product-image-wrap')) {
                e.preventDefault();
                e.stopPropagation();
                toggleCheck(e);
              }
            });
          }
        }
      });

      const addAllBtn = this.querySelector('[data-fbt-add-all]');
      if (addAllBtn) {
        addAllBtn.addEventListener('click', () => this.addAllToCart(addAllBtn));
      }
    }

    addAllToCart(button) {
      const selected = this.getSelectedProducts();
      const variantIds = selected.map((p) => p.variant_id).filter(Boolean);
      if (variantIds.length === 0) return;

      const allSelected = selected.length === this.products.length;
      const widgetId = this.dataset.widgetId || '';
      const sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
      const properties = (allSelected && widgetId)
        ? {
            _r_added_bundle_session_id: sessionId,
            _source: 'Rebuy',
            _widget_id: widgetId,
            _attribution: 'Rebuy Dynamic Product Bundle'
          }
        : null;

      const sectionsToRender = getSectionsToRender();
      const sections = sectionsToRender.map((s) => s.section);
      const sectionsUrl = window.location.pathname;
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');

      const items = variantIds.map((id) => {
        const item = { id: parseInt(id, 10), quantity: 1 };
        if (properties && Object.keys(properties).length > 0) {
          item.properties = properties;
        }
        return item;
      });

      const requestBody = {
        items: items,
        sections: sections,
        sections_url: sectionsUrl
      };

      fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status && data.status !== 200) {
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          if (data.sections) {
            renderSectionContents(data);
          }
          const drawer = document.getElementById('Cart-Drawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((err) => {
          console.error('FBT Add to cart:', err);
          if (typeof window.alert === 'function') {
            window.alert(err.message || 'Could not add all items to cart.');
          }
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;');
  }

  if (!customElements.get('frequently-bought-together')) {
    customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
  }
})();
